import { ApolloError } from "@apollo/client";
import { Link } from "@react-navigation/native";
import {
    Alert,
    Button,
    Headline,
    IconButton,
    Loading,
    PrinterWrapper,
    Spacer,
    StyleFunction,
    Surface,
    Text,
    TooltipErrorIcon,
    TooltipWarningIcon,
    useAlert,
    useAuth,
    useMerchantConfig,
    useModal,
    useTheme,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import {
    GQInvoiceHourSales,
    GQShiftQuery,
    useAddEconomicSyncJobsMutation,
    useIntegrationsOfTypeQuery,
    useShiftQuery,
} from "graphql-sdk";
import { formatAmount, formatDateTime, formatRelativeDate, isJSON } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import { useCallback } from "react";
import React, { View } from "react-native";

import {
    VictoryAxis,
    VictoryBar,
    VictoryChart,
    VictoryTheme,
    VictoryTooltip,
} from "victory";
import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { ShiftBalance } from "./shift-balance";
import { EconomicVoucherTextModal } from "./voucher-text-modal";
import { TouchableOpacity } from "react-native-gesture-handler";
import Clipboard from "@react-native-clipboard/clipboard";

type ScreenProps = RootStackScreenProps<"SHIFT">;

export function ShiftScreen({ route }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.shift");

    const [{ locale }] = useAdminSession(["locale"]);
    const [t] = useTranslation();
    const toast = useToast();
    const { render } = useModal();

    const shiftId = route.params.id;
    const { data, loading, error } = useShiftQuery({
        variables: {
            id: shiftId,
        },
        fetchPolicy: "no-cache",
    });

    const { data: integrationsData, loading: integrationLoading } =
        useIntegrationsOfTypeQuery({
            variables: { type: "E-CONOMIC" },
            fetchPolicy: "no-cache",
        });

    const currentEconomicIntegration =
        integrationsData?.integrationsOfType.data.find(
            itr => itr.active && itr.apiKey
        );

    const { data: fiskalyData, loading: fiskalyLoading } =
        useIntegrationsOfTypeQuery({
            variables: { type: "FISKALY" },
            fetchPolicy: "no-cache",
        });

    const currentFiskalyIntegration = fiskalyData?.integrationsOfType.data.find(
        itr => itr.active && itr.apiKey
    );

    const [addEconomicSyncJob] = useAddEconomicSyncJobsMutation();

    const handleAddEconomicSyncJob = useCallback(async () => {
        if (integrationLoading || !currentEconomicIntegration) {
            return;
        }

        // Ask for a text to use when sync'ing the shift
        let voucherTextModalResponse = await render(onClose => (
            <EconomicVoucherTextModal onClose={onClose} />
        ));

        if (
            !voucherTextModalResponse ||
            voucherTextModalResponse.sync === false
        ) {
            return;
        }

        const result = await addEconomicSyncJob({
            variables: {
                ids: [shiftId],
                integrationId: currentEconomicIntegration.id,
                voucherText: voucherTextModalResponse.voucherText,
            },
        });

        if (result && result.data.addEconomicSyncJobs.status === true) {
            toast.success(
                t(
                    "backoffice.shifts.add_syncjob_success",
                    "A synchronization of the shift was added to the queue."
                )
            );
        } else {
            toast.warning(
                t(
                    "backoffice.shifts.add_syncjob_failed",
                    "Adding a sync job failed. Try again later."
                )
            );
        }
    }, [
        addEconomicSyncJob,
        currentEconomicIntegration,
        integrationLoading,
        render,
        shiftId,
        t,
        toast,
    ]);

    return (
        <AdminContainer>
            <Shift
                loading={loading}
                error={error}
                data={data}
                locale={locale as AvailableLocale}
                hasEconomicSync={
                    !integrationLoading && !!currentEconomicIntegration
                }
                onAddEconomicSyncJob={handleAddEconomicSyncJob}
                hasFiskalySync={!fiskalyLoading && !!currentFiskalyIntegration}
            />
        </AdminContainer>
    );
}

const BAR_CHART_PADDING = {
    top: 10,
    bottom: 40,
    left: 100,
    right: 100,
};

function Shift({
    loading,
    locale,
    hasEconomicSync = false,
    hasFiskalySync = false,
    data,
    error,
    onAddEconomicSyncJob,
}: {
    loading: boolean;
    locale: AvailableLocale;
    hasEconomicSync: boolean;
    hasFiskalySync: boolean;
    data?: GQShiftQuery;
    error?: ApolloError;
    onAddEconomicSyncJob: () => void;
}) {
    const alert = useAlert();
    const toast = useToast();
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const { currency } = useMerchantConfig();
    const theme = useTheme();

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error.message,
                    }
                )}
            </Alert>
        );
    }

    if (!data?.shift) {
        return (
            <Alert type="info">
                {t("backoffice.shift.no_data", "There is no data to show.")}
            </Alert>
        );
    }

    // Copy the payments array and get payments from CLOSE
    const paymentTypeData = JSON.parse(
        JSON.stringify(
            data.shift.payments?.filter(itr => itr.event === "CLOSE")
        )
    );

    // Copy the payments array and update paymentTypes by subtracting cash from "OPEN"
    paymentTypeData.forEach(ptItr => {
        const foundPayments = JSON.parse(
            JSON.stringify(
                data.shift.payments.filter(
                    pItr =>
                        pItr.type === ptItr.type &&
                        pItr.name === ptItr.name &&
                        pItr.event === "OPEN"
                )
            )
        );

        const foundPaymentsAmount = foundPayments.reduce(
            (acc, cur) => acc + cur.amount,
            0
        );

        ptItr.amount -= foundPaymentsAmount;
    });

    // Copy the payments array and get CARD payment types.
    const cardPaymentSchemeData = JSON.parse(
        JSON.stringify(
            data.shift.payments?.filter(
                itr => itr.type === "CARD" && itr.cardSchemas !== undefined
            )
        )
    );

    const cardSchemeData = cardPaymentSchemeData?.reduce(
        (acc, cur) => acc.concat(acc, cur.cardSchemas!),
        [] as { name: string; amount: number; count: number }[]
    );

    const getStackedBarWidth = (
        width: number,
        datasets: GQInvoiceHourSales[]
    ) => {
        const setLength = datasets.length;
        const chartWidth =
            width - BAR_CHART_PADDING.left - BAR_CHART_PADDING.right;
        // Set the width of the bars so that they take up about 60% of the width
        return (chartWidth * 0.6) / setLength;
    };

    return (
        <View>
            <Surface style={styles.elementSpacing}>
                <View style={styles.surfaceContent}>
                    <View style={styles.columnContainer}>
                        <View style={styles.headerItem}>
                            <Headline size="h5" style={styles.headline}>
                                {t("backoffice.shifts.opened_at", "Opened")}
                            </Headline>
                            <Text>
                                {t(
                                    "backoffice.shift.opened_by_username",
                                    "{{date}} by {{username}}",
                                    {
                                        date: formatDateTime(
                                            data.shift.openedAt,
                                            locale
                                        ),
                                        username:
                                            data.shift.openedByUser?.username,
                                        interpolation: {
                                            escapeValue: false,
                                        },
                                    }
                                )}
                            </Text>
                        </View>
                        <Spacer />
                        <View style={styles.headerItem}>
                            <Headline size="h5" style={styles.headline}>
                                {t("backoffice.shifts.closed_at", "Closed")}
                            </Headline>
                            <Text>
                                {data.shift.closedAt
                                    ? t(
                                          "backoffice.shift.closed_by_username",
                                          "{{date}} by {{username}}",
                                          {
                                              date: formatDateTime(
                                                  data.shift.closedAt,
                                                  locale
                                              ),
                                              username:
                                                  data.shift.closedByUser
                                                      ?.username,
                                              interpolation: {
                                                  escapeValue: false,
                                              },
                                          }
                                      )
                                    : "-"}
                            </Text>
                        </View>
                        <Spacer />
                    </View>
                    <View style={styles.columnContainer}>
                        <View style={styles.headerItem}>
                            <Headline size="h5" style={styles.headline}>
                                {t(
                                    "backoffice.shifts.cash_register_name",
                                    "Cash register"
                                )}
                            </Headline>
                            <Link
                                to={{
                                    screen: "CASH_REGISTER_EDIT",
                                    params: { id: data.shift.cashRegister.id },
                                }}
                                style={styles.textLink}
                            >
                                <Text>
                                    {data.shift.cashRegisterName ||
                                        data.shift.cashRegister.name}
                                </Text>
                            </Link>
                        </View>
                        <Spacer />
                        {hasEconomicSync || hasFiskalySync ? (
                            <View style={styles.headerItem}>
                                <Headline size="h5" style={styles.headline}>
                                    {t(
                                        "backoffice.shifts.synced_at",
                                        "Sync'ed"
                                    )}
                                </Headline>
                                <View style={styles.row}>
                                    {data.shift.syncStatus === "SUCCESS" ? (
                                        data.shift.syncedAt ? (
                                            <Text>
                                                {formatDateTime(
                                                    data.shift.syncedAt,
                                                    locale
                                                )}
                                            </Text>
                                        ) : null
                                    ) : data.shift.syncStatus === "FAIL" ||
                                      data.shift.syncStatus === "ERROR" ? (
                                        <TouchableOpacity
                                            style={styles.row}
                                            onPress={async () => {
                                                const alertText = getAlertText(
                                                    data.shift.syncDetails,
                                                    t(
                                                        "backoffice.shifts.sync_unknown_details",
                                                        "Sync caused problems. Check the log."
                                                    )
                                                );

                                                if (!alertText) {
                                                    // If there is no text, then don't show an alert
                                                    return;
                                                }

                                                await alert(
                                                    t(
                                                        "backoffice.shift.sync_alert_header",
                                                        "Error message from sync'ing"
                                                    ),
                                                    alertText
                                                );
                                            }}
                                        >
                                            {data.shift.syncStatus ===
                                            "FAIL" ? (
                                                <TooltipWarningIcon
                                                    title={getAlertText(
                                                        data.shift.syncDetails,
                                                        t(
                                                            "backoffice.shifts.sync_unknown_details",
                                                            "Sync caused problems. Check the log."
                                                        )
                                                    )}
                                                    style={styles.noSpace}
                                                />
                                            ) : data.shift.syncStatus ===
                                              "ERROR" ? (
                                                <TooltipErrorIcon
                                                    title={getAlertText(
                                                        data.shift.syncDetails,
                                                        t(
                                                            "backoffice.shifts.sync_unknown_details",
                                                            "Sync caused problems. Check the log."
                                                        )
                                                    )}
                                                    style={styles.noSpace}
                                                />
                                            ) : null}
                                            {data.shift.syncedAt ? (
                                                <Text numberOfLines={1}>
                                                    {formatRelativeDate(
                                                        data.shift.syncedAt,
                                                        locale as AvailableLocale
                                                    )}
                                                </Text>
                                            ) : null}
                                        </TouchableOpacity>
                                    ) : null}
                                    {!data.shift.closedAt ? (
                                        "-"
                                    ) : hasEconomicSync ? (
                                        <Button
                                            iconName="sync"
                                            onPress={onAddEconomicSyncJob}
                                            size="tiny"
                                        >
                                            {t(
                                                "backoffice.shifts.sync",
                                                "Sync"
                                            )}
                                        </Button>
                                    ) : null}
                                </View>
                            </View>
                        ) : (
                            <View style={styles.headerItem} />
                        )}
                    </View>
                    <View style={styles.columnContainer}>
                        <View style={styles.headerItem}>
                            <Headline size="h5" style={styles.headline}>
                                {t(
                                    "backoffice.shifts.shift_number",
                                    "Shift no."
                                )}
                            </Headline>
                            <Text>{data.shift.shiftNumber || "-"}</Text>
                        </View>
                        <View style={styles.headerItem}>
                            <Headline size="h5" style={styles.headline}>
                                {t("backoffice.shifts.shift_id", "Shift ID")}
                            </Headline>
                            <View style={styles.row}>
                                <Text numberOfLines={1}>{data.shift.id}</Text>
                                <IconButton
                                    size="small"
                                    onPress={() => {
                                        Clipboard.setString(
                                            data.shift.id as string
                                        );
                                        toast.info(
                                            t(
                                                "backoffice.info.text_copied",
                                                "Text copied"
                                            )
                                        );
                                    }}
                                    name="copy"
                                    color="black"
                                />
                            </View>
                        </View>
                    </View>
                </View>
            </Surface>
            <Surface style={styles.elementSpacing}>
                <View style={styles.content}>
                    <View style={styles.contentItem}>
                        <Headline size="h5" style={styles.headline}>
                            {t("backoffice.shift.total_sales", "Total sales")}
                        </Headline>
                        <View style={styles.items}>
                            <Text style={styles.itemLeft}>
                                {t("common.total", "Total")}
                            </Text>
                            <Text style={styles.itemRight}>
                                {formatAmount(data.shift.amount, currency, {
                                    printCurrency: true,
                                })}
                            </Text>
                        </View>
                        <View style={styles.items}>
                            <Text style={styles.itemLeft}>
                                {t("common.discount", "Discount")}
                            </Text>
                            <Text style={styles.itemRight}>
                                {formatAmount(data.shift.discount, currency, {
                                    printCurrency: true,
                                })}
                            </Text>
                        </View>
                    </View>
                    <Spacer />
                    <ShiftBalance
                        data={data}
                        locale={locale}
                        currency={currency}
                    />
                </View>
            </Surface>
            <Surface style={styles.elementSpacing}>
                <Headline size="h4" style={styles.headline}>
                    {t(
                        "backoffice.shift.sales_by_productgroup",
                        "Sales by product group"
                    )}
                </Headline>
                <View style={styles.content}>
                    <View style={styles.contentItem}>
                        <View style={styles.items}>
                            <Text style={[styles.tableHeader, styles.itemLeft]}>
                                {t("common.product_group", "Product group")}
                            </Text>
                            <Text
                                style={[styles.tableHeader, styles.itemRight]}
                            >
                                {t("common.amount", "Amount")}
                            </Text>
                        </View>
                        {data.shift.salesData?.byProductGroup?.map(
                            (item, i) => (
                                <View
                                    style={styles.items}
                                    key={`product_group_${i}`}
                                >
                                    <Text style={styles.itemLeft}>
                                        {item.name}
                                    </Text>
                                    <Text style={styles.itemRight}>
                                        {formatAmount(item.amount, currency, {
                                            printCurrency: true,
                                        })}
                                    </Text>
                                </View>
                            )
                        )}
                    </View>
                    <View style={styles.contentItem}>
                        <VictoryChart
                            theme={VictoryTheme.material}
                            height={getBarChartHeight(
                                data.shift.salesData?.byProductGroup?.length
                            )}
                            width={500}
                            padding={BAR_CHART_PADDING}
                            domainPadding={{
                                x: 10,
                            }}
                        >
                            <VictoryAxis
                                dependentAxis
                                crossAxis={false} // Force showing of 0
                                tickCount={4}
                                fixLabelOverlap
                                tickFormat={tick => {
                                    return formatAmount(tick, currency);
                                }}
                            />
                            <VictoryAxis
                                tickFormat={_tick => {
                                    // No ticks
                                    return "";
                                }}
                            />
                            <VictoryBar
                                horizontal
                                data={data.shift.salesData?.byProductGroup?.reverse()}
                                labels={({ datum }) => datum.name}
                                y="amount"
                                x="name"
                                style={{
                                    data: {
                                        fill: ({ datum }) =>
                                            datum.amount > 0
                                                ? theme.colors.secondary
                                                : theme.colors.error,
                                    },
                                    labels: {
                                        fontSize: 8,
                                    },
                                }}
                            />
                        </VictoryChart>
                    </View>
                </View>
            </Surface>
            <Surface style={styles.elementSpacing}>
                <Headline size="h4" style={styles.headline}>
                    {t(
                        "backoffice.shift.sales_by_payment_type",
                        "Sales by payment type"
                    )}
                </Headline>
                {paymentTypeData.length === 0 ? (
                    <Text>
                        {t(
                            "backoffice.shift.no_payment_types_to_show",
                            "No payments methods to show"
                        )}
                    </Text>
                ) : (
                    <View style={styles.content}>
                        <View style={styles.contentItem}>
                            <View style={styles.items}>
                                <Text
                                    style={[
                                        styles.tableHeader,
                                        styles.itemLeft,
                                    ]}
                                >
                                    {t(
                                        "common.payment_method",
                                        "Payment method"
                                    )}
                                </Text>
                                <Text
                                    style={[
                                        styles.tableHeader,
                                        styles.itemRight,
                                    ]}
                                >
                                    {t("common.amount", "Amount")}
                                </Text>
                            </View>
                            {paymentTypeData.map((item, i) => (
                                <View
                                    style={styles.items}
                                    key={`product_group_${i}`}
                                >
                                    <Text style={styles.itemLeft}>
                                        {item.name}
                                    </Text>
                                    <Text style={styles.itemRight}>
                                        {formatAmount(item.amount, currency, {
                                            printCurrency: true,
                                        })}
                                    </Text>
                                </View>
                            ))}
                        </View>
                        <View style={styles.contentItem}>
                            <VictoryChart
                                horizontal
                                theme={VictoryTheme.material}
                                height={getBarChartHeight(
                                    paymentTypeData.length
                                )}
                                width={500}
                                padding={BAR_CHART_PADDING}
                                domainPadding={{ x: 10 }}
                            >
                                <VictoryAxis
                                    dependentAxis
                                    crossAxis={false} // Force showing of 0
                                    tickCount={2}
                                    tickFormat={tick => {
                                        return formatAmount(tick, currency);
                                    }}
                                />
                                <VictoryAxis
                                    tickFormat={_tick => {
                                        // No ticks
                                        return "";
                                    }}
                                />
                                <VictoryBar
                                    data={paymentTypeData.reverse()}
                                    labels={({ datum }) => datum.name}
                                    y="amount"
                                    x="name"
                                    style={{
                                        data: {
                                            fill: ({ datum }) =>
                                                datum.amount > 0
                                                    ? theme.colors.secondary
                                                    : theme.colors.error,
                                        },
                                        labels: {
                                            fontSize: 8,
                                        },
                                    }}
                                />
                            </VictoryChart>
                        </View>
                    </View>
                )}
            </Surface>

            <Surface style={styles.elementSpacing}>
                <Headline size="h4" style={styles.headline}>
                    {t(
                        "backoffice.shift.sales_by_card_scheme",
                        "Sales by card scheme"
                    )}
                </Headline>
                {cardSchemeData?.length === 0 ? (
                    <Text>
                        {t(
                            "backoffice.shift.no_card_schemes_to_show",
                            "No card schemes to show"
                        )}
                    </Text>
                ) : (
                    <View style={styles.content}>
                        <View style={styles.contentItem}>
                            <View style={styles.items}>
                                <Text
                                    style={[
                                        styles.tableHeader,
                                        styles.itemLeft,
                                    ]}
                                >
                                    {t(
                                        "backoffice.shift.card_scheme",
                                        "Card scheme"
                                    )}
                                </Text>
                                <Text
                                    style={[
                                        styles.tableHeader,
                                        styles.itemCenter,
                                    ]}
                                >
                                    {t("common.count", "Count")}
                                </Text>
                                <Text
                                    style={[
                                        styles.tableHeader,
                                        styles.itemRight,
                                    ]}
                                >
                                    {t("common.amount", "Amount")}
                                </Text>
                            </View>
                            {cardSchemeData?.map((item, i) => {
                                return (
                                    <View
                                        style={styles.items}
                                        key={`card_scheme_${i}`}
                                    >
                                        <Text style={styles.itemLeft}>
                                            {item.name}
                                        </Text>
                                        <Text style={styles.itemCenter}>
                                            {item.count}
                                        </Text>
                                        <Text style={styles.itemRight}>
                                            {formatAmount(
                                                item.amount,
                                                currency,
                                                {
                                                    printCurrency: true,
                                                }
                                            )}
                                        </Text>
                                    </View>
                                );
                            })}
                        </View>
                        <View style={styles.contentItem}>
                            <VictoryChart
                                horizontal
                                theme={VictoryTheme.material}
                                height={getBarChartHeight(
                                    cardSchemeData?.length || 0
                                )}
                                width={500}
                                padding={BAR_CHART_PADDING}
                                domainPadding={{ x: 10 }}
                            >
                                <VictoryAxis
                                    dependentAxis
                                    crossAxis={false} // Force showing of 0
                                    tickCount={2}
                                    tickFormat={tick => {
                                        return formatAmount(tick, currency);
                                    }}
                                />
                                <VictoryAxis
                                    tickFormat={_tick => {
                                        // No ticks
                                        return "";
                                    }}
                                />
                                <VictoryBar
                                    data={cardSchemeData}
                                    labels={({ datum }) => datum.name}
                                    y="amount"
                                    x="name"
                                    style={{
                                        data: {
                                            fill: ({ datum }) =>
                                                datum.amount > 0
                                                    ? theme.colors.secondary
                                                    : theme.colors.error,
                                        },
                                        labels: {
                                            fontSize: 8,
                                        },
                                    }}
                                />
                            </VictoryChart>
                        </View>
                    </View>
                )}
            </Surface>

            <Surface style={styles.elementSpacing}>
                <Headline size="h4" style={styles.headline}>
                    {t("backoffice.shift.hour_sales", "Sales by hour")}
                </Headline>
                <View style={styles.content}>
                    <View style={styles.contentItem}>
                        <View style={styles.items}>
                            <Text style={[styles.tableHeader, styles.itemLeft]}>
                                {t("common.hour", "Hour")}
                            </Text>
                            <Text
                                style={[styles.tableHeader, styles.itemRight]}
                            >
                                {t("common.count", "Count")}
                            </Text>
                            <Text
                                style={[styles.tableHeader, styles.itemRight]}
                            >
                                {t("common.amount", "Amount")}
                            </Text>
                        </View>
                        {data.shift.salesData?.hourSales?.map((item, i) => {
                            return (
                                <View
                                    style={styles.items}
                                    key={`hour_sales_${i}`}
                                >
                                    <Text style={styles.itemLeft}>
                                        {`${(
                                            "0" + new Date(item.date).getHours()
                                        ).slice(-2)}:00-${(
                                            "0" + new Date(item.date).getHours()
                                        ).slice(-2)}:59`}
                                    </Text>
                                    <Text style={styles.itemRight}>
                                        {item.count}
                                    </Text>
                                    <Text style={styles.itemRight}>
                                        {formatAmount(item.amount, currency, {
                                            printCurrency: true,
                                        })}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                    <View style={styles.contentItem}>
                        <VictoryChart
                            theme={VictoryTheme.material}
                            height={250}
                            width={500}
                            padding={{
                                top: 10,
                                bottom: 40,
                                left: 100,
                                right: 10,
                            }}
                            domainPadding={{
                                x:
                                    getStackedBarWidth(
                                        500,
                                        data.shift.salesData?.hourSales || []
                                    ) / 2,
                            }}
                        >
                            <VictoryAxis
                                dependentAxis
                                crossAxis={false} // Force showing of 0
                            />
                            <VictoryAxis
                                tickFormat={tick => {
                                    const date = new Date(tick);
                                    return `${("0" + date.getHours()).slice(
                                        -2
                                    )}:00-${("0" + date.getHours()).slice(
                                        -2
                                    )}:59`;
                                }}
                                style={{
                                    tickLabels: {
                                        angle: 45,
                                        padding: 12,
                                        fontSize: 8,
                                        transform: "translate(15, 3)",
                                    },
                                    axisLabel: {
                                        padding: 50,
                                    },
                                }}
                            />
                            <VictoryBar
                                data={data.shift.salesData?.hourSales || []}
                                labels={({ datum }) =>
                                    formatAmount(datum.amount, currency, {
                                        printCurrency: true,
                                    })
                                }
                                labelComponent={<VictoryTooltip />}
                                y={d => d.amount / 100}
                                x="date"
                                style={{
                                    data: {
                                        fill: ({ datum }) =>
                                            datum.amount > 0
                                                ? theme.colors.secondary
                                                : theme.colors.error,
                                    },
                                    labels: {
                                        fontSize: 8,
                                    },
                                }}
                            />
                        </VictoryChart>
                    </View>
                </View>
            </Surface>
            <Surface>
                <Headline size="h4" style={styles.headline}>
                    {t("backoffice.shift.z_report", "Z report")}
                </Headline>

                {data.shift.zReport ? (
                    <PrinterWrapper>
                        <Surface style={styles.reportWrapper}>
                            <Text style={styles.receipt}>
                                {data.shift.zReport}
                            </Text>
                        </Surface>
                    </PrinterWrapper>
                ) : (
                    <>
                        <Spacer />
                        <View>
                            <Alert type="info">
                                {t(
                                    "backoffice.shift.not_closed_yet",
                                    "There is no Z report yet, since the shift is not closed."
                                )}
                            </Alert>
                        </View>
                    </>
                )}
            </Surface>
        </View>
    );
}

const getBarChartHeight = (numberOfItems: number = 1) => {
    const rowHeight = 15;
    const minHeight = 50;
    return numberOfItems * rowHeight + minHeight;
};

const styleFunc: StyleFunction = (theme, dimensions) => ({
    textLink: {
        ...theme.styles.link,
    },

    backButton: {
        width: theme.spacingScale * 10,
        marginBottom: theme.spacingScale,
    },
    elementSpacing: { marginBottom: theme.spacingScale * 2 },
    header: {
        flexDirection: "row",
        flexWrap: "wrap",
    },
    content: {
        flexDirection: "row",
        flexWrap: "wrap",
    },
    contentItem: {
        flex: 1,
    },
    headerItem: { flex: 1 },
    tableHeader: {
        ...theme.styles.text,
        ...theme.fonts.medium,
        lineHeight: 32,
    },
    items: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
    itemLeft: { flex: 3 },
    itemCenter: { flex: 2, textAlign: "center" },
    itemRight: { flex: 2, textAlign: "right" },
    headline: {
        marginBottom: 0,
    },
    receipt: {
        ...theme.fonts.monospace,
        fontSize: 14,
    },

    reportWrapper: {
        padding: theme.spacingScale * 2,
        borderWidth: 1,
        borderColor: theme.colors.grey250,
        borderRadius: 0,

        maxWidth: theme.dimensions.maxFormWidth,
        elevation: 3,
    },

    surfaceContent: {
        ...theme.styles.flex1,
        flexDirection: dimensions.width < 600 ? "column" : "row",
    },
    columnContainer: {
        flex: 1,
        flexDirection: dimensions.width < 960 ? "column" : "row",
    },
    row: {
        ...theme.styles.row,
        alignItems: "center",
        gap: theme.spacingScale,
    },
    oddRow: {
        ...theme.styles.oddRow,
    },
    success: {
        color: theme.colors.success,
    },
    warning: {
        color: theme.colors.warning,
    },
    error: {
        color: theme.colors.error,
    },

    rowCenter: {
        alignItems: "center",
    },

    noSpace: {
        paddingHorizontal: 0,
    },
});

export function getAlertText(
    errorDetails: string,
    defaultErrorMessage: string
): string {
    if (
        !errorDetails ||
        typeof errorDetails !== "string" ||
        !isJSON(errorDetails)
    ) {
        return defaultErrorMessage;
    }

    const detailsObject = JSON.parse(errorDetails);

    const errors = detailsObject?.errors;

    if (!errors) {
        // Fallback, if there are no field errors
        if (detailsObject.message) {
            return `${detailsObject.message} (${detailsObject.errorCode})`;
        }

        return defaultErrorMessage;
    }

    const errorsArray = errors.map(errorObj => {
        const fields = Object.getOwnPropertyNames(errorObj);

        return fields
            .map(field => {
                if (field !== "account" && field !== "accountingYear") {
                    return;
                }

                // Return an array with the field name and a joint string of the errors for that field.
                return [
                    field,
                    errorObj[field].errors.map(
                        e =>
                            `${e.inputValue}: ${e.errorMessage} (Code: ${e.errorCode})`
                    ),
                ];
            })
            .filter(Boolean);
    });

    const alertText = errorsArray.map(_errors =>
        _errors.map(
            (errorField, _index) =>
                `${
                    errorField[0].charAt(0).toLocaleUpperCase() +
                    errorField[0].slice(1)
                }:\n${errorField[1]?.join("\n")}`
        )
    );

    return alertText;
}
